import {
  chatApiRequestSchema,
  chatInitiateResponseSchema,
  chatFinalResponseSchema,
  chatReactionCreatePayloadSchema,
  chatReactionUpdatePayloadSchema,
  messageIdGenerationSchema,
  taskIdSchema,
  reactionParamsSchema,
  chatHistoryApiResponseSchema,
  chatHistoryPresignedItemSchema,
  parseChatHistoryMessages,
  chatReactionsResponseSchema,
  type ChatFinalResponse,
  type ChatReactionCreatePayload,
  type ChatReactionUpdatePayload,
  type ChatHistoryMessage,
  type ChatReactionRecord,
} from '../../components/Chatbot/ChatSchemas';
import { isAllowedChatMediaUrl } from '../../components/Chatbot/chatMediaUrl';
import {
  chatDebugError,
  chatDebugWarn,
  describeHistoryEntry,
  describeUnknownPayload,
} from './chatDebug';
import { chatbotConfig } from './chatbotConfig';
import { getCookie } from '../../shared/utils';

const POLLING_INTERVAL_MS = 5000;
const MAX_POLLING_ATTEMPTS = 120;

const STATUS_CODE_ACCEPTED = 202;
const STATUS_CODE_SUCCESS = 200;
const STATUS_CODE_ERROR = 500;

const MESSAGE_ID_STORE_KEY = 'chat_reaction_message_ids';
const ALLOWED_REACTION_CATEGORIES = ['meds', 'stats', 'staff'] as const;
type ReactionCategory = (typeof ALLOWED_REACTION_CATEGORIES)[number];
const MAX_MESSAGE_LENGTH = 1000;

const HASH_SHIFT_AMOUNT = 5;
const HASH_FALLBACK_VALUE = 1;

const INFLIGHT_REQUESTS = new Map<string, Promise<void>>();

export interface ChatApiRequest {
  query: string;
  customerId: number;
  role: string;
  token: string;
}

export interface ChatApiResponse {
  response: string;
  report?: boolean;
  fileLink?: string[];
  apiExplanation?: Record<string, unknown>;
  contentBlocks?: ChatFinalResponse['contentBlocks'];
  sources?: ChatFinalResponse['sources'];
}

export interface ChatApiError {
  detail: Array<{
    loc: (string | number)[];
    msg: string;
    type: string;
  }>;
}

export const initiateChatTask = async (
  request: ChatApiRequest,
): Promise<string> => {
  const initiateUrl = chatbotConfig.CHAT_INITIATE_API_URL;

  if (!initiateUrl) {
    throw new Error('Chat initiate API URL is not configured');
  }

  const backendRequest = {
    query: request.query,
    role: request.role,
  };

  const validatedRequest = chatApiRequestSchema.parse(backendRequest);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (request.token) {
      headers['Authorization'] = `Bearer ${request.token}`;
    }

    headers['customer-id'] = String(request.customerId);

    const response = await fetch(initiateUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(validatedRequest),
    });

    if (!response.ok) {
      console.error(`Failed to initiate chat: ${response.status}`);
      if (response.status === 401 || response.status === 403) {
        throw new Error(
          'Authentication failed. Please check your credentials.',
        );
      } else if (response.status >= 500) {
        throw new Error(
          'Service temporarily unavailable. Please try again later.',
        );
      }
      throw new Error('Something went wrong. Please try again.');
    }

    const rawData = await response.json();
    const data = chatInitiateResponseSchema.parse(rawData);

    if (data.statusCode !== STATUS_CODE_ACCEPTED) {
      console.error('Unexpected response from initiate endpoint');
      throw new Error('Something went wrong. Please try again.');
    }

    return data.taskId;
  } catch (error) {
    console.error('Error initiating chat:', error);
    if (error instanceof TypeError) {
      throw new Error(
        'Unable to connect. Please check your internet connection.',
      );
    }
    if (
      error instanceof Error &&
      error.message.includes('Authentication failed')
    ) {
      throw error;
    }
    if (
      error instanceof Error &&
      error.message.includes('Service temporarily unavailable')
    ) {
      throw error;
    }
    throw new Error('Something went wrong. Please try again.');
  }
};

const fetchHistoryFromPresignedUrl = async (
  presignedUrl: string,
): Promise<ChatHistoryMessage[]> => {
  if (!isAllowedChatMediaUrl(presignedUrl)) {
    console.error('Blocked untrusted chat history presigned URL');
    return [];
  }

  const response = await fetch(presignedUrl);

  if (!response.ok) {
    console.error(
      `Failed to fetch chat history from presigned URL: ${response.status}`,
    );
    return [];
  }

  // S3 may serve the file as text or as a double-encoded JSON string
  // (e.g. "\"{\\\"history\\\": [...]}\""), so normalize before schema parse.
  const rawText = await response.text();

  let rawData: unknown;
  try {
    rawData = JSON.parse(rawText);
  } catch {
    console.error('Failed to parse chat history presigned payload');
    return [];
  }

  // Unwrap nested JSON strings (double / triple encoded).
  while (typeof rawData === 'string') {
    try {
      rawData = JSON.parse(rawData);
    } catch {
      console.error('Failed to parse nested chat history JSON string');
      return [];
    }
  }

  const rawMessages: unknown = Array.isArray(rawData)
    ? rawData
    : (rawData as { history?: unknown })?.history;

  if (!Array.isArray(rawMessages)) {
    console.error(
      `Chat history presigned payload has no history array: ${describeUnknownPayload(rawData)}`,
    );
    chatDebugError(
      'Chat history presigned payload has no history array',
      rawData,
    );
    return [];
  }

  // Parse each entry independently so one malformed record does not drop all history.
  const messages = rawMessages.flatMap((entry) => {
    const result = chatHistoryPresignedItemSchema.safeParse(entry);
    if (!result.success) {
      console.warn(
        `Skipping unparseable chat history entry: ${describeHistoryEntry(entry)}`,
      );
      chatDebugWarn('Skipping unparseable chat history entry', entry);
      return [];
    }
    return [result.data];
  });

  return parseChatHistoryMessages(messages);
};

export const getChatHistory = async (
  customerId: number,
  token?: string,
): Promise<ChatHistoryMessage[]> => {
  const historyUrl = chatbotConfig.CHAT_HISTORY_API_URL;

  if (!customerId || customerId <= 0) {
    throw new Error('Valid customer ID is required to fetch chat history');
  }

  const url = `${historyUrl}?customer_id=${encodeURIComponent(customerId)}`;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    headers['customer-id'] = String(customerId);

    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      console.error(`Failed to fetch chat history: ${response.status}`);
      if (response.status === 401 || response.status === 403) {
        throw new Error(
          'Authentication failed. Please check your credentials.',
        );
      } else if (response.status === 404) {
        throw new Error('No chat history found.');
      } else if (response.status >= 500) {
        throw new Error(
          'Service temporarily unavailable. Please try again later.',
        );
      }
      throw new Error('Something went wrong. Please try again.');
    }

    const rawText = await response.text();

    let rawData: unknown;
    try {
      rawData = JSON.parse(rawText);
    } catch {
      console.error('Failed to parse chat history response');
      return [];
    }

    // Unwrap nested JSON strings (History API sometimes returns a JSON string body).
    while (typeof rawData === 'string') {
      try {
        rawData = JSON.parse(rawData);
      } catch {
        console.error('Failed to parse nested chat history JSON string');
        return [];
      }
    }

    const apiResult = chatHistoryApiResponseSchema.safeParse(rawData);

    if (apiResult.success) {
      if (apiResult.data.history.length > 0) {
        return parseChatHistoryMessages(apiResult.data.history);
      }

      if (apiResult.data.presigned_url) {
        return fetchHistoryFromPresignedUrl(apiResult.data.presigned_url);
      }

      return [];
    }

    // Fallback: response shape differs (e.g. bare array or history without customer_id).
    const presignedUrl = (rawData as { presigned_url?: unknown })
      ?.presigned_url;
    const rawMessages: unknown = Array.isArray(rawData)
      ? rawData
      : (rawData as { history?: unknown })?.history;

    if (Array.isArray(rawMessages) && rawMessages.length > 0) {
      const messages = rawMessages.flatMap((entry) => {
        const result = chatHistoryPresignedItemSchema.safeParse(entry);
        return result.success ? [result.data] : [];
      });
      return parseChatHistoryMessages(messages);
    }

    if (typeof presignedUrl === 'string' && presignedUrl) {
      return fetchHistoryFromPresignedUrl(presignedUrl);
    }

    console.error(
      `Unrecognized chat history response shape: ${describeUnknownPayload(rawData)}`,
    );
    chatDebugError('Unrecognized chat history response shape', rawData);
    return [];
  } catch (error) {
    console.error('Error fetching chat history:', error);
    if (error instanceof TypeError) {
      throw new Error(
        'Unable to connect. Please check your internet connection.',
      );
    }
    if (
      error instanceof Error &&
      (error.message.includes('Authentication failed') ||
        error.message.includes('No chat history'))
    ) {
      throw error;
    }
    throw new Error('Something went wrong. Please try again.');
  }
};

export const getChatReactions = async (
  customerId: number,
  token?: string,
): Promise<ChatReactionRecord[]> => {
  const reactionApiUrl = chatbotConfig.REACTION_API_URL;
  if (!reactionApiUrl) {
    throw new Error('Reaction API URL is not configured');
  }

  const url = new URL(reactionApiUrl);
  url.searchParams.append('customer_ids', String(customerId));

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.error(`Failed to fetch chat reactions: ${response.status}`);
    return [];
  }

  const rawData = await response.json();
  return chatReactionsResponseSchema.parse(rawData);
};

const extractMessageIdFromResponse = (data: unknown): number | null => {
  if (!data) {
    return null;
  }

  const records = Array.isArray(data) ? data : [data];
  const first = records[0];
  if (
    first &&
    typeof first === 'object' &&
    'message_id' in first &&
    typeof (first as { message_id: unknown }).message_id === 'number'
  ) {
    return (first as { message_id: number }).message_id;
  }

  return null;
};

const syncMessageIdFromServer = async (
  frontendId: string,
  messageContent: string,
  customerId: number,
  authToken?: string,
): Promise<number | null> => {
  const reactions = await getChatReactions(customerId, authToken);
  const normalizedContent = truncateMessageContent(messageContent);
  const match = reactions.find(
    (reaction) =>
      truncateMessageContent(reaction.message) === normalizedContent,
  );

  if (!match) {
    return null;
  }

  storeMessageId(frontendId, match.message_id);
  return match.message_id;
};

export const getChatResponse = async (
  taskId: string,
  token: string | undefined,
  customerId: number,
): Promise<ChatFinalResponse> => {
  const baseUrl = chatbotConfig.CHAT_RESPONSE_API_URL;

  if (!baseUrl) {
    throw new Error('Chat response API URL is not configured');
  }

  const validatedTaskId = taskIdSchema.parse(taskId);

  const getChatUrl = `${baseUrl}?task_id=${encodeURIComponent(
    validatedTaskId,
  )}`;

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    headers['customer-id'] = String(customerId);

    const response = await fetch(getChatUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      console.error(`Failed to get chat response: ${response.status}`);
      if (response.status === 401 || response.status === 403) {
        throw new Error(
          'Authentication failed. Please check your credentials.',
        );
      } else if (response.status === 404) {
        throw new Error(
          'Response not found. Please try sending your message again.',
        );
      } else if (response.status >= 500) {
        throw new Error(
          'Service temporarily unavailable. Please try again later.',
        );
      }
      throw new Error('Something went wrong. Please try again.');
    }

    const rawData = await response.json();
    return chatFinalResponseSchema.parse(rawData);
  } catch (error) {
    console.error('Error getting chat response:', error);
    if (error instanceof TypeError) {
      throw new Error(
        'Unable to connect. Please check your internet connection.',
      );
    }
    if (
      error instanceof Error &&
      (error.message.includes('Authentication failed') ||
        error.message.includes('Response not found') ||
        error.message.includes('Service temporarily unavailable'))
    ) {
      throw error;
    }
    throw new Error('Something went wrong. Please try again.');
  }
};

export const sendChatMessage = async (
  request: ChatApiRequest,
  onProgress?: (message: string) => void,
): Promise<ChatApiResponse> => {
  const taskId = await initiateChatTask(request);
  let pollCount = 0;

  const statusMessages = [
    'Thinking...',
    'Analyzing your request...',
    'Fetching insights...',
    'Almost there...',
  ];

  if (onProgress) {
    onProgress(statusMessages[0]);
  }

  return new Promise((resolve, reject) => {
    const pollTimer = setInterval(async () => {
      try {
        pollCount++;

        const result = await getChatResponse(
          taskId,
          request.token,
          request.customerId,
        );

        if (result.statusCode === STATUS_CODE_SUCCESS) {
          clearInterval(pollTimer);
          resolve({
            response: result.response,
            report: result.report,
            fileLink: result.fileLink,
            apiExplanation: result.apiExplanation,
            contentBlocks: result.contentBlocks,
            sources: result.sources,
          });
        } else if (result.statusCode === STATUS_CODE_ERROR) {
          clearInterval(pollTimer);
          reject(new Error(result.response || 'Chat processing failed'));
        } else {
          if (onProgress) {
            const messageIndex = pollCount % statusMessages.length;
            onProgress(statusMessages[messageIndex]);
          }
        }

        if (pollCount >= MAX_POLLING_ATTEMPTS) {
          clearInterval(pollTimer);
          reject(new Error('Request timed out. Please try again.'));
        }
      } catch (error) {
        clearInterval(pollTimer);
        reject(error);
      }
    }, POLLING_INTERVAL_MS);
  });
};

export type { ChatReactionCreatePayload, ChatReactionUpdatePayload };

export const truncateMessageContent = (content: string): string => {
  const trimmed = content.trim();
  return trimmed.length > MAX_MESSAGE_LENGTH
    ? `${trimmed.substring(0, MAX_MESSAGE_LENGTH)}...`
    : trimmed;
};

export const buildAssistantMessageId = (
  content: string,
  customerId: number,
): string => `assistant-${generateMessageId(content, customerId)}`;

export const reactionToFeedback = (
  reaction: 'liked' | 'disliked',
  favourite: boolean,
) => ({
  liked: reaction === 'liked',
  disliked: reaction === 'disliked',
  favorited: favourite,
});

export const linkReactionRecordsToMessages = (
  messages: Array<{ id: string; role: string; content: string }>,
  reactions: ChatReactionRecord[],
): void => {
  const reactionsByContent = new Map(
    reactions.map((reaction) => [
      truncateMessageContent(reaction.message),
      reaction,
    ]),
  );

  messages.forEach((message) => {
    if (message.role !== 'assistant') {
      return;
    }

    const reaction = reactionsByContent.get(
      truncateMessageContent(message.content),
    );
    if (reaction) {
      storeMessageId(message.id, reaction.message_id);
    }
  });
};

type ChatReactionPayload =
  ChatReactionCreatePayload | ChatReactionUpdatePayload;

function generateMessageId(content: string, senderId: number): number {
  const validated = messageIdGenerationSchema.parse({ content, senderId });

  const input = `${validated.senderId}|${validated.content.trim()}`;
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << HASH_SHIFT_AMOUNT) - hash + input.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) || HASH_FALLBACK_VALUE;
}

function loadMessageIdStore(): Record<string, number> {
  try {
    if (typeof window === 'undefined') {
      return {};
    }

    const raw = localStorage.getItem(MESSAGE_ID_STORE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    if (
      typeof parsed !== 'object' ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      console.warn('Invalid message_id store format, resetting');
      return {};
    }

    return parsed;
  } catch (error) {
    console.error('Error loading message_id store:', error);
    return {};
  }
}

function saveMessageIdStore(store: Record<string, number>): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }

    if (!store || typeof store !== 'object' || Array.isArray(store)) {
      console.error('Invalid store object, skipping save');
      return;
    }

    const serialized = JSON.stringify(store);
    localStorage.setItem(MESSAGE_ID_STORE_KEY, serialized);
  } catch (error) {
    console.error('Failed to persist message_id store:', error);
  }
}

function storeMessageId(frontendId: string, backendMessageId: number): void {
  const store = loadMessageIdStore();
  store[frontendId] = backendMessageId;
  saveMessageIdStore(store);
}

function getStoredMessageId(frontendId: string): number | null {
  const store = loadMessageIdStore();
  return store[frontendId] ?? null;
}

export const sendChatReaction = async (
  message: { id: string; content: string; timestamp: Date },
  reaction: 'liked' | 'disliked',
  favourite: boolean,
  customerId: string | number,
  category?: string,
  authToken?: string,
): Promise<void> => {
  const reactionApiUrl = chatbotConfig.REACTION_API_URL;
  if (!reactionApiUrl) {
    throw new Error('Reaction API URL is not configured');
  }

  const existingRequest = INFLIGHT_REQUESTS.get(message.id);
  if (existingRequest) {
    console.log('Waiting for in-flight request to complete...');
    await existingRequest;
  }

  const requestPromise = (async () => {
    const validated = reactionParamsSchema.parse({
      customerId,
      message,
      reaction,
      favourite,
    });

    const senderIdNum = validated.customerId;
    const truncatedMessage = truncateMessageContent(validated.message.content);

    const sendReactionRequest = async (
      requestPayload: ChatReactionPayload,
    ): Promise<Response> => {
      const token = authToken ?? getCookie('access_token') ?? undefined;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      return fetch(reactionApiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify([requestPayload]),
      });
    };

    const persistServerMessageId = async (
      response: Response,
    ): Promise<void> => {
      let responseData: unknown;
      try {
        responseData = await response.clone().json();
      } catch {
        responseData = null;
      }

      const responseMessageId = extractMessageIdFromResponse(responseData);
      if (responseMessageId) {
        storeMessageId(message.id, responseMessageId);
        return;
      }

      await syncMessageIdFromServer(
        message.id,
        truncatedMessage,
        senderIdNum,
        authToken ?? getCookie('access_token') ?? undefined,
      );
    };

    let storedMessageId = getStoredMessageId(message.id);
    if (!storedMessageId) {
      storedMessageId = await syncMessageIdFromServer(
        message.id,
        truncatedMessage,
        senderIdNum,
        authToken ?? getCookie('access_token') ?? undefined,
      );
    }

    let payload: ChatReactionPayload;

    if (storedMessageId) {
      payload = chatReactionUpdatePayloadSchema.parse({
        message_id: storedMessageId,
        reaction,
        favourite,
      });
    } else {
      const validCategory =
        category &&
        ALLOWED_REACTION_CATEGORIES.includes(category as ReactionCategory)
          ? (category as ReactionCategory)
          : undefined;

      payload = chatReactionCreatePayloadSchema.parse({
        message: truncatedMessage,
        metadata: {
          timestamp: message.timestamp.toISOString(),
          sender_id: Math.trunc(senderIdNum),
        },
        reaction,
        favourite,
        ...(validCategory ? { category: validCategory } : {}),
      });
    }

    let response = await sendReactionRequest(payload);

    if (!response.ok && 'message_id' in payload && response.status === 400) {
      const syncedId = await syncMessageIdFromServer(
        message.id,
        truncatedMessage,
        senderIdNum,
        authToken ?? getCookie('access_token') ?? undefined,
      );

      if (syncedId) {
        payload = chatReactionUpdatePayloadSchema.parse({
          message_id: syncedId,
          reaction,
          favourite,
        });
        response = await sendReactionRequest(payload);
      }
    }

    if (!response.ok) {
      let errorBody: string | undefined;
      try {
        errorBody = await response.text();
      } catch {}
      console.error(
        'Reaction API error:',
        errorBody ?? `status ${response.status}`,
      );

      const isDuplicateError =
        errorBody &&
        (errorBody.toLowerCase().includes('duplicate') ||
          errorBody.toLowerCase().includes('already exists'));

      if (isDuplicateError && 'message' in payload) {
        const syncedId = await syncMessageIdFromServer(
          message.id,
          truncatedMessage,
          senderIdNum,
          authToken ?? getCookie('access_token') ?? undefined,
        );

        if (syncedId) {
          const retryPayload = chatReactionUpdatePayloadSchema.parse({
            message_id: syncedId,
            reaction,
            favourite,
          });
          const retryResponse = await sendReactionRequest(retryPayload);

          if (!retryResponse.ok) {
            throw new Error(
              `Failed to save reaction after retry: ${retryResponse.status}`,
            );
          }

          await persistServerMessageId(retryResponse);
          return;
        }
      }

      throw new Error('Something went wrong. Please try again.');
    }

    await persistServerMessageId(response);
  })();

  INFLIGHT_REQUESTS.set(message.id, requestPromise);

  try {
    await requestPromise;
  } finally {
    INFLIGHT_REQUESTS.delete(message.id);
  }
};
